import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import PropertyCard from '@/components/PropertyCard';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Search as SearchIcon, Filter, X } from 'lucide-react';

const Search = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [cities, setCities] = useState([]);

  const [filters, setFilters] = useState({
    city: searchParams.get('city') || '',
    category: searchParams.get('category') || '',
    project_status: searchParams.get('project_status') || '',
    price_min: searchParams.get('price_min') || '',
    price_max: searchParams.get('price_max') || '',
    q: searchParams.get('q') || ''
  });

  useEffect(() => {
    loadCities();
  }, []);

  useEffect(() => {
    search();
  }, [searchParams]);

  const loadCities = async () => {
    try {
      const { data } = await api.get('/public/cities');
      setCities(data);
    } catch (e) {}
  };

  const search = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(Object.fromEntries(searchParams)).forEach(([k, v]) => {
        if (v) params.append(k, v);
      });
      const { data } = await api.get(`/public/projects?${params.toString()}`);
      setProjects(data.projects);
      setTotal(data.total);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v) params.append(k, v);
    });
    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilters({ city: '', category: '', project_status: '', price_min: '', price_max: '', q: '' });
    setSearchParams(new URLSearchParams());
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="font-display font-bold text-2xl sm:text-3xl text-[#0F2C59]">Search Properties</h1>
          <p className="text-sm text-slate-500 mt-1">{total} projects found {filters.city && `in ${filters.city}`}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* FILTERS */}
          <aside className="lg:col-span-3">
            <div className="bg-white rounded-lg border border-slate-200 p-5 sticky top-20">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-sm flex items-center gap-2"><Filter size={16} /> Filters</h3>
                <button onClick={clearFilters} className="text-xs text-[#F97316] font-semibold hover:underline" data-testid="clear-filters-btn">Clear</button>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Search</Label>
                  <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Keywords..." className="mt-1" data-testid="filter-q-input" />
                </div>

                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">City</Label>
                  <Select value={filters.city} onValueChange={(v) => setFilters({ ...filters, city: v })}>
                    <SelectTrigger className="mt-1" data-testid="filter-city-select"><SelectValue placeholder="Any city" /></SelectTrigger>
                    <SelectContent>
                      {cities.map(c => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Category</Label>
                  <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
                    <SelectTrigger className="mt-1" data-testid="filter-category-select"><SelectValue placeholder="Any" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="residential">Residential</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                      <SelectItem value="plot">Plot</SelectItem>
                      <SelectItem value="land">Land</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Status</Label>
                  <Select value={filters.project_status} onValueChange={(v) => setFilters({ ...filters, project_status: v })}>
                    <SelectTrigger className="mt-1" data-testid="filter-status-select"><SelectValue placeholder="Any status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new_launch">New Launch</SelectItem>
                      <SelectItem value="under_construction">Under Construction</SelectItem>
                      <SelectItem value="ready_to_move">Ready to Move</SelectItem>
                      <SelectItem value="resale">Resale</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Min ₹</Label>
                    <Input type="number" value={filters.price_min} onChange={(e) => setFilters({ ...filters, price_min: e.target.value })} placeholder="0" className="mt-1" data-testid="filter-price-min-input" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Max ₹</Label>
                    <Input type="number" value={filters.price_max} onChange={(e) => setFilters({ ...filters, price_max: e.target.value })} placeholder="Any" className="mt-1" data-testid="filter-price-max-input" />
                  </div>
                </div>

                <Button onClick={applyFilters} className="w-full bg-[#0F2C59] hover:bg-[#0F2C59]/90" data-testid="apply-filters-btn">Apply Filters</Button>
              </div>
            </div>
          </aside>

          {/* RESULTS */}
          <div className="lg:col-span-9">
            {loading ? (
              <div className="text-center py-16 text-slate-500">Loading...</div>
            ) : projects.length === 0 ? (
              <div className="bg-white rounded-lg border border-dashed border-slate-300 p-12 text-center">
                <SearchIcon size={40} className="mx-auto text-slate-300 mb-3" />
                <div className="text-slate-500 font-medium">No projects found</div>
                <div className="text-xs text-slate-400 mt-1">Try adjusting your filters</div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {projects.map(project => (
                  <PropertyCard key={project.id} project={project} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Search;
